import { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Typography,
  Button,
  Divider,
  MenuItem,
  TextField,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import CustomTextInput from '../../components/Forms/CustomTextInput';
import CustomSwitch from '../../components/Forms/CustomSwitch';
import InventoryBarcodes from '../../components/MenuNew/FormView/Inventory/InventoryBarcodes';
import { UOM_OPTIONS } from '../../components/MenuNew/FormView/Inventory/configs';
import { priceTransform } from '../../helpers/formPrice';
import {
  createProduct,
  updateProduct,
  getCategoriesByMenu,
  getProductById,
  addInventoryStock,
  removeInventoryStock,
  adjustInventoryStock,
} from '../../redux/actions/inventory/inventory';
import { getMenusV2 } from '../../redux/actions/Menu/menuV2';
import {
  selectCategoriesByMenu,
  selectCurrentBusiness,
  selectProductsByMenu,
} from '../../redux/selectors';
import { checkDuplicateBarcodes } from '../../utils/checkDuplicateBarcodes';
import ImageUpload from '../../components/FormFields/ImageUpload';
import StockActionModal from '../../components/Inventory/StockActionModal';
import DuplicateBarcodeModal from '../../components/Inventory/DuplicateBarcodeModal';
import { confirm } from '../../components/MenuNew/ConfirmModal';
import PromotionTab from '../PosSettings/Promotions/PromotionTab';
import usePromotionsEnabled from 'hooks/usePromotionsEnabled';

const InventoryItemForm = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const dispatch = useDispatch();
  const categoriesByMenu = useSelector(selectCategoriesByMenu);
  const currentBusiness = useSelector(selectCurrentBusiness);
  const productsByMenu = useSelector(selectProductsByMenu);

  const [loading, setLoading] = useState(false);
  const [stockActionLoading, setStockActionLoading] = useState(false);
  const [model, setModel] = useState({});
  const [quantity, setQuantity] = useState(0);
  const [currentItem, setCurrentItem] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null); // 'remove', 'add', or 'adjust'
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [keepLogoOriginalSize, setKeepLogoOriginalSize] = useState(false);
  const [duplicateBarcodeModalOpen, setDuplicateBarcodeModalOpen] =
    useState(false);
  const [duplicateItems, setDuplicateItems] = useState([]);
  const [pendingSaveData, setPendingSaveData] = useState(null);
  const [originalBarcodes, setOriginalBarcodes] = useState([]);
  const isEdit = Boolean(id);
  const promotionsEnabled = usePromotionsEnabled();

  // Create validation schema conditionally based on edit mode
  const validationSchema = yup.object({
    name: yup.string().required('Please enter a product name'),
    // Required for new items. Legacy items that were saved without a category
    // stay editable, so it is only enforced on create.
    category: isEdit
      ? yup.string().nullable()
      : yup.string().required('Please select a category'),
    price: yup
      .mixed()
      .required('Please enter a price')
      .test(
        'is-number',
        'Please enter a valid price (must be 0 or greater)',
        (value) => {
          if (!value || value === '') return false;
          const numValue =
            typeof value === 'string' ? parseFloat(value) : value;
          return !isNaN(numValue) && numValue >= 0;
        }
      ),
    cost: yup
      .mixed()
      .nullable()
      .test(
        'is-number',
        'Please enter a valid cost (must be 0 or greater)',
        (value) => {
          if (value === '' || value === null || value === undefined)
            return true;
          const numValue =
            typeof value === 'string' ? parseFloat(value) : value;
          return !isNaN(numValue) && numValue >= 0;
        }
      ),
    quantity: isEdit
      ? yup.mixed().nullable()
      : yup
          .mixed()
          .nullable()
          .transform((value, originalValue) => {
            // Transform empty string to null to prevent NaN errors
            if (
              originalValue === '' ||
              originalValue === null ||
              originalValue === undefined
            ) {
              return null;
            }
            // Convert string to number if possible
            const numValue =
              typeof originalValue === 'string'
                ? parseFloat(originalValue)
                : originalValue;
            return isNaN(numValue) ? null : numValue;
          })
          .test(
            'is-number',
            'Please enter a valid quantity (must be 0 or greater)',
            (value) => {
              // Allow null/undefined (will default to 0)
              if (value === null || value === undefined) return true;
              return typeof value === 'number' && value >= 0;
            }
          ),
    sku: yup.string().nullable(),
    unit: yup.string().required('Please select a unit of measure'),
    // Barcodes are optional. When none are provided the backend keeps its
    // existing behavior of generating one from the SKU.
    barcodes: yup.array().of(yup.string()).nullable(),
    trackInventory: yup.boolean(),
  });

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: '',
      category: '',
      price: '',
      cost: '',
      quantity: '',
      sku: '',
      unit: 'each',
      barcodes: [],
      image: null,
      trackInventory: true,
    },
  });

  const { setValue, trigger, formState } = methods;
  const isDirty = formState.isDirty;

  const onChangePrice = async (e) => {
    const { name, value } = e.target;
    const transformedValue = priceTransform(value);
    setValue(name, transformedValue, { shouldDirty: true });

    // Trigger validation to update error state
    await trigger(name);
  };

  const onChangeCost = async (e) => {
    const { name, value } = e.target;
    const transformedValue = priceTransform(value);
    setValue(name, transformedValue, { shouldDirty: true });

    // Trigger validation to update error state
    await trigger(name);
  };

  // Handle browser back/close with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue =
          'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  // Fetch categories and products on mount
  useEffect(() => {
    dispatch(getCategoriesByMenu());
    // No need to get products here since inventory list is already available
  }, [dispatch]);

  // Reset keepLogoOriginalSize when productId changes
  useEffect(() => {
    setKeepLogoOriginalSize(false);
  }, [id]);

  // Load item data for editing
  useEffect(() => {
    if (isEdit && id) {
      setLoading(true);
      dispatch(getProductById(id))
        .then((response) => {
          const productData = response?.data?.data;
          if (productData) {
            setCurrentItem(productData);
            setQuantity(productData.inventoryInfo?.stock || 0);
            const initialBarcodes = productData.barcodeNumbers || [];
            setOriginalBarcodes(initialBarcodes);
            methods.reset({
              name: productData.name || '',
              category: productData.category?._id || productData.category || '',
              price: String(productData.price) || '0',
              cost: String(productData.inventoryInfo?.unitPrice || '0'),
              sku: productData.inventoryInfo?.sku || '',
              unit: productData.inventoryInfo?.unit || productData.unit || '',
              barcodes: initialBarcodes,
              image: productData.image || null,
              trackInventory:
                productData.inventoryInfo?.trackInventory ?? false,
            });
            setModel({ image: productData.image });
          }
        })
        .catch((error) => {
          console.error('Error loading product:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isEdit, id, methods, dispatch]);

  const performSave = async (formData) => {
    if (isEdit) {
      await dispatch(updateProduct(id, formData));
    } else {
      await dispatch(createProduct(formData));
    }

    // Update original barcodes after successful save
    if (formData.barcodeNumbers) {
      setOriginalBarcodes(formData.barcodeNumbers);
    }

    // Refresh menu data after successful save
    await dispatch(getMenusV2(false));

    // Reset dirty state after successful save
    methods.reset(methods.getValues());
    history.goBack();
  };

  const handleSave = async (data) => {
    console.log('Form data:', data);
    setLoading(true);
    try {
      const formData = {
        name: data.name,
        price: parseFloat(data.price || '0'),
        barcodeNumbers: data.barcodes || [],
        unit: data.unit,
        trackInventory: data.trackInventory ?? false,
      };

      // Always set on create, where the field is required. On edit a legacy
      // item may still have no category: the API rejects an empty categoryId
      // but leaves the current one untouched when it is omitted.
      if (data.category) {
        formData.categoryId = data.category;
      }

      // SKU is optional. Only send it when the user typed one; when blank the
      // backend keeps auto-generating a SKU.
      if (data.sku && data.sku.trim()) {
        formData.sku = data.sku.trim();
      }

      // Add unitPrice to inventoryInfo if cost is provided
      if (data.cost !== undefined && data.cost !== '') {
        formData.unitPrice = parseFloat(data.cost);
      }

      // Only add image if it exists
      if (data.image) {
        formData.image = data.image;
      }

      // Only add stock in add new mode
      if (!isEdit) {
        formData.stock = Number(data.quantity) || 0;
      }

      // Check for duplicate barcodes using frontend data
      // Only check newly added barcodes, not already saved ones
      const currentBarcodes = formData.barcodeNumbers || [];
      const normalizedCurrentBarcodes = currentBarcodes
        .map((b) => String(b).trim())
        .filter((b) => b);
      const normalizedOriginalBarcodes = originalBarcodes
        .map((b) => String(b).trim())
        .filter((b) => b);

      // Find newly added barcodes (in current but not in original)
      const originalBarcodesSet = new Set(normalizedOriginalBarcodes);
      const newlyAddedBarcodes = normalizedCurrentBarcodes.filter(
        (barcode) => !originalBarcodesSet.has(barcode)
      );

      // Only check for duplicates if there are newly added barcodes
      // For new items (not edit), check all barcodes
      const barcodesToCheck = isEdit
        ? newlyAddedBarcodes
        : normalizedCurrentBarcodes;

      if (barcodesToCheck.length > 0) {
        const duplicateItemsData = checkDuplicateBarcodes(
          productsByMenu || [],
          barcodesToCheck,
          isEdit ? id : null
        );

        if (duplicateItemsData.length > 0) {
          // Store the form data and show duplicate modal
          setPendingSaveData(formData);
          setDuplicateItems(duplicateItemsData);
          setDuplicateBarcodeModalOpen(true);
          setLoading(false);
          return;
        }
      }

      // No duplicates found, proceed with save
      await performSave(formData);
    } catch (error) {
      console.error('Error saving item:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContinueWithDuplicate = async () => {
    setDuplicateBarcodeModalOpen(false);
    if (pendingSaveData) {
      setLoading(true);
      try {
        await performSave(pendingSaveData);
      } catch (error) {
        console.error('Error saving item:', error);
      } finally {
        setLoading(false);
        setPendingSaveData(null);
        setDuplicateItems([]);
      }
    }
  };

  const handleCancelDuplicate = () => {
    setDuplicateBarcodeModalOpen(false);
    setPendingSaveData(null);
    setDuplicateItems([]);
  };

  const handleCancel = async () => {
    if (isDirty) {
      const result = await confirm({
        title: 'confirm_modal.title',
        message: 'You have unsaved changes. Are you sure you want to leave?',
        cancelText: 'confirm_modal.cancel',
        confirmText: 'confirm_modal.save_and_leave',
        discardText: 'confirm_modal.discard',
        hideCloseIcon: false,
        hideDiscardBtn: false,
        background: '#1976d2',
      });
      if (result === true) {
        // Save and leave - save first then go back
        const formData = methods.getValues();
        await handleSave(formData);
      } else if (result === 'discard') {
        // Discard changes and go back
        history.goBack();
      }
      // If result is false, user cancelled - do nothing
    } else {
      history.goBack();
    }
  };

  const handleActionClick = (action) => {
    setSelectedAction(action);
    setStockModalOpen(true);
  };

  const handleCloseStockModal = () => {
    setStockModalOpen(false);
    setSelectedAction(null);
  };

  const handleStockAction = async (stockQuantity) => {
    if (!isEdit || !currentItem || !selectedAction) return;

    try {
      setStockActionLoading(true);
      const inventory = currentItem?.inventoryInfo || {};

      const requestData = {
        uuid: inventory.uuid || currentItem.uuid || '',
        stock: Number(stockQuantity) || 0,
        businessUuid: currentBusiness?.uuid || '',
      };

      let apiCall;
      switch (selectedAction) {
        case 'add':
          apiCall = addInventoryStock(requestData);
          break;
        case 'remove':
          apiCall = removeInventoryStock(requestData);
          break;
        case 'adjust':
          apiCall = adjustInventoryStock(requestData);
          break;
        default:
          throw new Error('Invalid action');
      }

      await dispatch(apiCall);
      const response = await dispatch(getProductById(id));
      const productData = response?.data?.data;
      if (productData?.inventoryInfo?.stock !== undefined) {
        setQuantity(productData.inventoryInfo.stock);
      }
      handleCloseStockModal();
    } catch (error) {
      console.error('Error updating stock:', error);
    } finally {
      setStockActionLoading(false);
    }
  };

  // Prepare categories for dropdown.
  const categoryOptions = Array.isArray(categoriesByMenu)
    ? categoriesByMenu.map((cat) => ({
        value: cat._id || cat.id,
        label: cat.name,
      }))
    : [];

  // Prepare UOM options for dropdown
  const unitOptions = UOM_OPTIONS.map((unit) => ({
    value: unit.value,
    label: unit.label,
  }));

  return (
    <FormProvider {...methods}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        {/* Form Container */}
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: 1,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            component="form"
            onSubmit={methods.handleSubmit(handleSave, (errors) => {
              console.log('Form validation errors:', errors);
              // You can add toast/alert here to show errors to user
            })}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Title */}
            <Typography
              sx={{
                mb: 2,
                fontWeight: '400',
                fontSize: '22px',
              }}
            >
              {isEdit ? t('Edit Item') : t('Add New')}
            </Typography>
            <Divider sx={{ mb: 4 }} />

            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {/* Item Photo Section */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 3,
                }}
              >
                <ImageUpload
                  setFormValue={methods.setValue}
                  initialValue={model?.image}
                  name="image"
                  model={model}
                  setModel={setModel}
                  className="d-block mb-3"
                  size={108}
                  removable
                  keepLogoOriginalSize={keepLogoOriginalSize}
                />
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 'bold', mb: 1 }}
                  >
                    {t('Item Photo')}
                  </Typography>
                  <Typography sx={{ fontSize: '14px', color: '#929398' }}>
                    JPG, JPEG or PNG.
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ mb: 3 }}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={keepLogoOriginalSize}
                        onChange={(event) =>
                          setKeepLogoOriginalSize(event.target.checked)
                        }
                      />
                    }
                    label="Keep original size"
                  />
                </FormGroup>
              </Box>

              {/* Form Container with fixed width */}
              <Box sx={{ width: '606px' }}>
                {/* Form Fields */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}
                >
                  {/* First row: Name and Category */}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      width: '100%',
                      flexDirection: 'row',
                    }}
                  >
                    <CustomTextInput
                      sx={{
                        width: '100%',
                      }}
                      name="name"
                      label={`${t('Name')} *`}
                      disabled={loading}
                    />
                    <CustomTextInput
                      sx={{ width: '100%' }}
                      select
                      name="category"
                      label={`${t('Category')} *`}
                      disabled={loading}
                    >
                      {categoryOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </CustomTextInput>
                  </Box>

                  {/* Second row: Price and Cost */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <CustomTextInput
                      sx={{
                        width: '100%',
                      }}
                      name="price"
                      label={`${t('Price')} *`}
                      inputProps={{ type: 'number', step: 'any' }}
                      InputProps={{
                        startAdornment: (
                          <Typography sx={{ mr: 1 }}>$</Typography>
                        ),
                      }}
                      onChange={onChangePrice}
                      disabled={loading || stockActionLoading}
                    />
                    <CustomTextInput
                      sx={{
                        width: '100%',
                      }}
                      name="cost"
                      label={t('Avg Cost')}
                      inputProps={{ type: 'number', step: 'any' }}
                      InputProps={{
                        startAdornment: (
                          <Typography sx={{ mr: 1 }}>$</Typography>
                        ),
                      }}
                      onChange={onChangeCost}
                      disabled={loading || stockActionLoading}
                    />
                    {isEdit && (
                      <TextField
                        sx={{ width: '100%' }}
                        size="small"
                        variant="outlined"
                        label={t('Last Invoice Cost')}
                        InputLabelProps={{ shrink: true }}
                        value={
                          currentItem?.inventoryInfo?.metadata
                            ?.lastInvoiceUnitPrice != null
                            ? String(
                                currentItem.inventoryInfo.metadata
                                  .lastInvoiceUnitPrice
                              )
                            : ''
                        }
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <Typography sx={{ mr: 1 }}>$</Typography>
                          ),
                        }}
                        disabled
                      />
                    )}
                  </Box>

                  {/* Third row: Stock/Quantity (only in add mode) */}
                  {!isEdit && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 2,
                      }}
                    >
                      <CustomTextInput
                        sx={{ width: '100%' }}
                        name="quantity"
                        label={t('Quantity')}
                        placeholder="0"
                        inputProps={{ type: 'number', min: 0 }}
                        disabled={loading}
                      />
                    </Box>
                  )}

                  {/* Fourth row: SKU and Unit of Measure */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <CustomTextInput
                        sx={{
                          width: '100%',
                        }}
                        name="sku"
                        label={t('SKU')}
                        placeholder={t('Auto-generated if left blank')}
                        disabled={loading}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <CustomTextInput
                        sx={{
                          width: '100%',
                        }}
                        select
                        name="unit"
                        label={`${t('Unit of Measure')} *`}
                        disabled={loading}
                      >
                        {unitOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </CustomTextInput>
                    </Box>
                  </Box>

                  {/* Fifth row: Barcodes (multi-select) */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <InventoryBarcodes
                        name="barcodes"
                        label={t('Barcodes')}
                        control={methods.control}
                        readOnly={false}
                        isDisabled={loading}
                      />
                    </Box>
                  </Box>

                  {/* Sixth row: Track Inventory Switch */}
                  <Box sx={{ display: 'flex' }}>
                    <Box sx={{ width: '100%' }}>
                      <CustomSwitch
                        name="trackInventory"
                        label={t('Track Inventory')}
                        disabled={loading}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Form Action Buttons (Cancel and Save) */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'flex-end',
                    mt: 3,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={loading}
                    sx={{ minWidth: 100 }}
                  >
                    {t('CANCEL')}
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={loading || !isDirty}
                    sx={{ minWidth: 100 }}
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                  >
                    {t('SAVE')}
                  </Button>
                </Box>
                {/* Divider between form and quantity/actions section */}
                {isEdit && <Divider sx={{ my: 3 }} />}

                {/* Quantity with Action Buttons (only in edit mode) */}
                {isEdit && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 2,
                    }}
                  >
                    {/* Quantity field */}
                    <TextField
                      sx={{ flex: 1 }}
                      label={t('Quantity')}
                      type="number"
                      value={quantity}
                      inputProps={{ min: 0, readOnly: true }}
                      focused={false}
                      disabled={true}
                      size="small"
                    />
                    {/* Action Buttons */}
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        flexDirection: 'row',
                        flex: 1,
                        justifyContent: 'space-between',
                      }}
                    >
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => handleActionClick('remove')}
                        size="small"
                        sx={{ flex: 1, height: '40px' }}
                        disabled={loading || stockActionLoading}
                      >
                        {t('REMOVE')}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => handleActionClick('add')}
                        size="small"
                        sx={{ flex: 1, height: '40px' }}
                        disabled={loading || stockActionLoading}
                      >
                        {t('ADD')}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => handleActionClick('adjust')}
                        size="small"
                        sx={{ flex: 1, height: '40px' }}
                        disabled={loading || stockActionLoading}
                      >
                        {t('ADJUST')}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {isEdit && promotionsEnabled && (
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 2,
              boxShadow: 1,
              p: 2,
            }}
          >
            <Typography sx={{ fontWeight: '400', fontSize: '18px', mb: 2 }}>
              {t('Promotions')}
            </Typography>
            <PromotionTab
              lockedProductIds={[currentItem?.uuid]}
              lockedProductName={currentItem?.name || methods.getValues().name}
            />
          </Box>
        )}
      </Box>

      {/* Stock Action Modal */}
      <StockActionModal
        open={stockModalOpen}
        onClose={handleCloseStockModal}
        action={selectedAction}
        onSave={handleStockAction}
        loading={stockActionLoading}
      />

      {/* Duplicate Barcode Modal */}
      <DuplicateBarcodeModal
        open={duplicateBarcodeModalOpen}
        onClose={handleCancelDuplicate}
        onContinue={handleContinueWithDuplicate}
        duplicateItems={duplicateItems}
      />
    </FormProvider>
  );
};

export default InventoryItemForm;
